import { useQuery } from "@tanstack/react-query";
import HttpClient from "../utils/network";
import { OnSiteApi } from "../apis/onsite";
import { ListResponse } from "../models/response";
import { Artist } from "../models/artist";
import { CacheKey, StoreKey } from "../models/cacheKey";
import { useAtom } from "jotai";
import { currentArtistAtom, mainArtistAtom } from "../atoms/artistAtom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect } from "react";

export function useArtists() {
  const [currentArtist, setCurrentArtist] = useAtom(currentArtistAtom);
  const [mainArtistId, setMainArtistId] = useAtom(mainArtistAtom);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryArtistId = Number(searchParams.get(StoreKey.queryArtist) || -1);

  const {
    isLoading,
    isSuccess,
    isError,
    error,
    data: artists,
  } = useQuery<Artist[]>({
    queryKey: [CacheKey.getArtists],
    queryFn: async () => {
      const response = new ListResponse(
        await HttpClient.request(OnSiteApi.artists()),
      );
      if (Array.isArray(response.data)) {
        return response.data.map((data) => {
          return new Artist(data);
        });
      } else {
        throw new Error(`Can not get artists from server`);
      }
    },
    staleTime: 60000,
  });

  const indexArtistInList = (artists: Artist[], artistId: number) => {
    return artists ? artists.findIndex((a) => a.id === artistId) : -1;
  };

  const initArtist = () => {
    // setup current artist
    let curArtist;
    if (!artists || artists.length === 0) {
      curArtist = undefined;
      setCurrentArtist(curArtist);
    } else {
      // exists artist query
      if (queryArtistId >= 0) {
        const artistIdx = indexArtistInList(artists, queryArtistId);
        if (artistIdx >= 0) {
          curArtist = artists[artistIdx];
          setCurrentArtist(curArtist);
        } else {
          navigate("/");
        }
      } else {
        // not exists artist query or invalid artist query
        // not current artist or invalid current artist
        if (
          !currentArtist ||
          indexArtistInList(artists, currentArtist.id) === -1
        ) {
          // try to set artist with main
          if (mainArtistId) {
            curArtist = artists.find((artist) => artist.id === mainArtistId);
          }
          // main artist is invalid
          if (!curArtist) {
            curArtist = artists[0];
            setMainArtistId(curArtist.id);
          }

          setCurrentArtist(curArtist);
        }
      }
    }
  };

  useEffect(initArtist, [artists, queryArtistId]);

  return { isLoading, isSuccess, isError, artists, error };
}
